// pages/Home.js
import React, { useRef, useEffect } from 'react';
import { Container, Typography, Button, Grid, Box, Card, CardContent } from '@mui/material';
import { Link } from 'react-router-dom';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ServiceCard from '../components/ServiceCard';
import { motion, useInView } from 'framer-motion';
import UltraStaticSparklinks from '../components/UltraStaticSparklinks';
// add these near your other @mui imports
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import EntrepreneurMotivation from '../components/EntreprenuerMotivation';
// icons used inside the accordions
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsappFloatingButton from '../components/WhatsappFloatingButton';
import logo from "../images/brand.png";
// Import Material-UI icons for services
import {
    Code,
    Cloud,
    Build,
    Palette,
    School,
    LaptopMac,
    Security,
    Storage,
    Api,
    DesignServices,
    ConnectWithoutContact
} from '@mui/icons-material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
// Import 3D components
import DynamicSphere from '../components/3d/DynamicSphere';
import FloatingParticles from '../components/3d/FloatingParticles';
import InteractiveCube from '../components/3d/InteractiveCube';
import founder from "../images/10045.jpeg";
import NextGenAIVision from '../components/NextGenAIVision';

const Home = () => {
    const services = [
        {
            title: 'Software Development',
            description: 'Custom software solutions designed to streamline business processes and drive measurable growth.',
            icon: Code,
            features: ['Web Applications', 'Mobile Applications', 'Enterprise Software', 'API Development & Integration'],
            detailedDescription: 'We deliver scalable, secure, and modern software tailored to your business needs. From full-stack development to mobile apps and API integrations, we ensure efficient operations and sustainable growth.',
            color: '#a8dadc' // Light Blue
        },
        {
            title: 'Web Development',
            description: 'Building responsive, high-performance websites and web applications tailored to your brand.',
            icon: LaptopMac,
            features: ['Responsive Web Design', 'Web Applications', 'UI/UX Optimization', 'Content Management Systems'],
            detailedDescription: 'Our team creates fast, secure, and intuitive websites and portals. We focus on modern design, usability, and performance to enhance engagement and deliver seamless user experiences.',
            color: '#ffe5b4' // Light Orange/Peach
        },
        {
            title: 'IT Consulting',
            description: 'Expert advisory to optimize IT infrastructure, enhance security, and drive digital transformation.',
            icon: ConnectWithoutContact,
            features: ['IT Strategy Planning', 'System Architecture', 'Digital Transformation', 'Cybersecurity & Compliance'],
            detailedDescription: 'Our consultants provide strategic guidance to align technology with business goals. We help modernize IT systems, improve security, and implement processes that maximize efficiency and growth potential.',
            color: '#f8c8dc' // Light Pink
        },
        {
            title: 'Web Hosting and Security',
            description: 'Reliable and secure hosting solutions for uninterrupted digital presence and business continuity.',
            icon: Security,
            features: ['Cloud Hosting', 'VPS Hosting', 'Database Management', '24/7 Monitoring & Support'],
            detailedDescription: 'We provide scalable hosting solutions with high availability and robust security. Automated backups, disaster recovery, and performance optimization ensure your websites and applications run smoothly.',
            color: '#c6f1d6' // Light Green
        },
        {
            title: 'Branding and Design',
            description: 'Creative visual solutions to build strong brand identities and engaging user experiences.',
            icon: Palette,
            features: ['Logo & Identity Design', 'UI/UX Design', 'Marketing Collateral', 'Digital Graphics & Assets'],
            detailedDescription: 'We craft cohesive brand systems and visually engaging experiences. From logos to UI/UX and marketing materials, our designs enhance brand recognition and support business objectives.',
            color: '#d9c6f5' // Light Purple
        },
        {
            title: 'Internship and Training',
            description: 'Structured programs for skill development, professional training, and future talent acquisition.',
            icon: School,
            features: ['Technical Workshops', 'Online Courses', 'Skill Development Programs', 'Certifications & Mentorship'],
            detailedDescription: 'We provide hands-on training, workshops, and internships to upskill employees and students. Our programs foster practical experience, professional growth, and help identify high-potential talent.',
            color: '#fbe8c6' // Light Yellow/Beige
        }
    ];

    const heroRef = useRef(null);
    const servicesRef = useRef(null);
    const isHeroInView = useInView(heroRef, { once: true, amount: 0.3 });
    const isServicesInView = useInView(servicesRef, { once: true, amount: 0.2 });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: {
            opacity: 0,
            y: 60,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94]
            }
        }
    };

    const floatingAnimation = {
        animate: {
            y: [0, -20, 0],
            transition: {
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    const staggerGridVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    const gridItemVariants = {
        hidden: {
            opacity: 0,
            y: 40,
            scale: 0.9
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };
    const productsRef = useRef(null);
    const isProductsInView = useInView(productsRef, { once: true, amount: 0.3 });


    return (
        <Box sx={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '90vh',
            background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 50%, #0a1128 100%)'
        }}>

            {/* 3D Floating Particles */}
            <FloatingParticles count={50} />
            <WhatsappFloatingButton />
            {/* Hero Section */}
            <Container
                ref={heroRef}
                maxWidth="xl"
                sx={{
                    minHeight: '70vh',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    py: { xs: 4, md: 8 },
                    mt: { xs: 8, sm: 5 }
                }}
            >
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={isHeroInView ? "visible" : "hidden"}
                    style={{ width: '100%' }}
                >
                    <Grid container spacing={6} alignItems="center" justifyContent="center">
                        <Grid item xs={12} md={6} lg={5}>
                            <motion.div variants={itemVariants}>
                                <Typography
                                    variant="h1"
                                    component="h1"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '1.75rem', md: '2.5rem', lg: '4rem' },
                                        background: 'linear-gradient(135deg, #8b0000 0%, #ff4d4d 50%, #ff9999 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        color: 'transparent',
                                        textShadow: '0 4px 30px rgba(139, 0, 0, 0.4)',
                                        lineHeight: 1.1,
                                        mb: 3,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Transform. Grow.
                                    <motion.span
                                        style={{
                                            background: 'linear-gradient(135deg, #ff4d4d, #ff9999)',
                                            backgroundClip: 'text',
                                            display: 'inline-block'
                                        }}
                                        animate={{
                                            scale: [1, 1.05, 1],
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        Succeed.
                                    </motion.span>
                                </Typography>
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        flexWrap: "wrap",
                                    }}
                                >

                                    {/* Brand Text */}
                                    <Typography
                                        variant="h4"
                                        component="p"
                                        gutterBottom
                                        sx={{
                                            mb: 2,
                                            color: "rgba(255,255,255,0.95)",
                                            fontWeight: 400,
                                            fontSize: { xs: "1.3rem", md: "1.6rem" },
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        Empowering businesses with{" "}
                                        <span
                                            style={{
                                                color: "#ff3131",
                                                fontWeight: 700,
                                                textShadow: "0 2px 10px rgba(255, 77, 77, 0.3)",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            Zolang Nexora Softwares
                                        </span>
                                    </Typography>



                                </Box>
                            </motion.div>


                            <motion.div variants={itemVariants}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mb: 5,
                                        color: 'rgba(255,255,255,0.85)',
                                        fontSize: '1.15rem',
                                        lineHeight: 1.7,
                                        maxWidth: '520px',
                                        fontWeight: 300,
                                    }}
                                >Research & Development Partner building intelligent systems and governed software solutions for long-term institutional impact.</Typography>
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                    position: 'relative',
                                    zIndex: 2,
                                    alignItems: 'center'
                                }}>
                                    <motion.div
                                        whileHover={{
                                            scale: 1.05,
                                            transition: { duration: 0.2 }
                                        }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            component={Link}
                                            to="/services"
                                            endIcon={<RocketLaunchIcon />}
                                            sx={{
                                                background: 'linear-gradient(135deg, #ff3333 0%, #cc0000 100%)',
                                                px: { xs: 3, md: 4 },
                                                py: 1.5,
                                                borderRadius: 3,
                                                fontWeight: 'bold',
                                                fontSize: { xs: '0.95rem', md: '1.05rem' },
                                                textTransform: 'none',
                                                boxShadow: '0 6px 24px rgba(255, 51, 51, 0.3)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #cc0000 0%, #ff3333 100%)',
                                                    boxShadow: '0 8px 32px rgba(255, 51, 51, 0.4)',
                                                    transform: 'translateY(-1px)',
                                                },
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                flex: { xs: '1 1 100%', sm: 'none' },
                                                minWidth: { xs: '100%', sm: '180px' }
                                            }}
                                        >
                                            Explore to Grow
                                        </Button>
                                    </motion.div>

                                    <motion.div
                                        whileHover={{
                                            scale: 1.05,
                                            transition: { duration: 0.2 }
                                        }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <Button
                                            variant="outlined"
                                            size="medium"
                                            component={Link}
                                            to="/contact"
                                            sx={{
                                                border: '2px solid rgba(255, 51, 51, 0.5)',
                                                color: 'rgba(255,255,255,0.95)',
                                                px: { xs: 3, md: 4 },
                                                py: 1.5,
                                                borderRadius: 3,
                                                fontWeight: 'bold',
                                                fontSize: { xs: '0.95rem', md: '1.05rem' },
                                                textTransform: 'none',
                                                background: 'rgba(255, 51, 51, 0.08)',
                                                backdropFilter: 'blur(8px)',
                                                '&:hover': {
                                                    border: '2px solid #ff3333',
                                                    background: 'rgba(255, 51, 51, 0.15)',
                                                    boxShadow: '0 6px 24px rgba(255, 51, 51, 0.25)',
                                                    transform: 'translateY(-1px)',
                                                },
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                flex: { xs: '1 1 100%', sm: 'none' },
                                                minWidth: { xs: '100%', sm: '160px' }
                                            }}
                                        >
                                            Get In Touch
                                        </Button>
                                    </motion.div>
                                </Box>
                            </motion.div>

                            {/* Stats Section */}
                            <motion.div
                                variants={itemVariants}
                                style={{ marginTop: '3rem' }}
                            >
                                <Grid container spacing={3}>
                                    {[
                                        { number: 'T', label: 'Technology-Driven Solutions' },
                                        { number: 'E', label: 'Engineering Excellence' },
                                        { number: 'C', label: 'Customer-Centric Support' },
                                        { number: 'H', label: 'High-Quality Delivery' }




                                    ].map((stat, index) => (
                                        <Grid item xs={6} sm={3} key={index}>
                                            <motion.div
                                                whileHover={{ scale: 1.1 }}
                                                style={{ textAlign: 'center' }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        background: 'linear-gradient(135deg, #ff4d4d, #ff9999)',
                                                        backgroundClip: 'text',
                                                        WebkitBackgroundClip: 'text',
                                                        color: 'transparent',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {stat.number}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.7)',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {stat.label}
                                                </Typography>

                                            </motion.div>
                                        </Grid>


                                    ))}
                                </Grid>
                                <Typography
                                    sx={{
                                        mt: 2,
                                        fontSize: "0.85rem",
                                        color: "rgba(255,255,255,0.65)",
                                        fontStyle: "italic",
                                        textAlign: "center",
                                        letterSpacing: "0.04em"
                                    }}
                                >
                                    “தெய்வத்தான் ஆகாதெனினும் முயற்சித்
                                    தன் மெய்வருத்தக் கூலி தரும்”
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.4,
                                        fontSize: "0.75rem",
                                        color: "rgba(255,255,255,0.55)",
                                        textAlign: "center"
                                    }}
                                >
                                    “Even when fate does not help, true effort always earns its reward.”
                                </Typography>

                            </motion.div>
                        </Grid>

                        {/* 3D Graphics Section */}
                        <Grid item xs={12} md={6} lg={7}>
                            <motion.div
                                variants={floatingAnimation}
                                animate="animate"
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                {/* Interactive 3D Sphere */}
                                <DynamicSphere />

                                {/* Floating Cubes */}
                                <Box sx={{
                                    position: 'absolute',
                                    top: '20%',
                                    left: '10%',
                                    width: '80px',
                                    height: '80px'
                                }}>
                                    <InteractiveCube size={80} color="#ff4d4d" delay={0} />
                                </Box>

                                <Box sx={{
                                    position: 'absolute',
                                    bottom: '30%',
                                    right: '15%',
                                    width: '60px',
                                    height: '60px'
                                }}>
                                    <InteractiveCube size={60} color="#8b0000" delay={1} />
                                </Box>

                                <Box sx={{
                                    position: 'absolute',
                                    top: '40%',
                                    right: '25%',
                                    width: '40px',
                                    height: '40px'
                                }}>
                                    <InteractiveCube size={40} color="#ff9999" delay={2} />
                                </Box>
                            </motion.div>
                        </Grid>
                    </Grid>
                </motion.div>
            </Container>
            <NextGenAIVision />
            {/* Services Preview Section */}
            <Container
                ref={servicesRef}
                maxWidth="xl"
                sx={{
                    py: { xs: 8, md: 12 },
                    position: 'relative'
                }}
            >
                {/* ===== HEADER ===== */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isServicesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <Box textAlign="center" mb={8}>
                        <Typography
                            variant="h2"
                            align="center"
                            sx={{
                                fontWeight: 800,
                                mb: 2,
                                background:
                                    'linear-gradient(135deg, #8b0000, #ff4d4d, #ff9999)',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                                fontSize: { xs: '2.4rem', md: '3.6rem' },
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Our Services
                        </Typography>

                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontWeight: 300,
                                fontSize: { xs: '1.15rem', md: '1.4rem' },
                                maxWidth: 620,
                                mx: 'auto',
                                lineHeight: 1.5
                            }}
                        >
                            Comprehensive IT solutions designed to accelerate business growth
                        </Typography>
                    </Box>
                </motion.div>

                {/* ===== SERVICES GRID ===== */}
                <motion.div
                    variants={staggerGridVariants}
                    initial="hidden"
                    animate={isServicesInView ? 'visible' : 'hidden'}
                >
                    <Grid container spacing={4} justifyContent="center">
                        {services.map((service, index) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                lg={4}
                                key={index}
                                sx={{ display: 'flex' }}
                            >
                                <motion.div
                                    variants={gridItemVariants}
                                    whileHover={{ y: -6 }}
                                    transition={{ duration: 0.25 }}
                                    style={{ width: '100%' }}
                                >
                                    <ServiceCard service={service} index={index} />
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                {/* ===== CTA ===== */}
                <Box textAlign="center" mt={10}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={
                            isServicesInView
                                ? { opacity: 1, scale: 1 }
                                : { opacity: 0, scale: 0.95 }
                        }
                        transition={{ duration: 0.6, delay: 0.4 }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/services"
                            endIcon={<RocketLaunchIcon />}
                            sx={{
                                border: '2px solid rgba(139,0,0,0.75)',
                                color: 'rgba(255,255,255,0.95)',
                                px: 7,
                                py: 1.8,
                                borderRadius: 3,
                                fontWeight: 700,
                                fontSize: '1.05rem',
                                textTransform: 'none',
                                background: 'rgba(139,0,0,0.12)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    borderColor: '#8b0000',
                                    background: 'rgba(139,0,0,0.22)',
                                    boxShadow: '0 10px 32px rgba(139,0,0,0.35)'
                                },
                                transition: 'all 0.25s ease'
                            }}
                        >
                            View All Services
                        </Button>
                    </motion.div>
                </Box>
            </Container>

            {/* ================= PRODUCTS SECTION ================= */}
            <Container
                ref={productsRef}
                maxWidth="xl"
                sx={{
                    py: { xs: 10, md: 14 },
                    position: "relative",
                }}
            >
                {/* Ambient background */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(circle at 20% 20%, rgba(255,77,77,0.08), transparent 60%)",
                        pointerEvents: "none",
                    }}
                />

                {/* ===== Header ===== */}
                <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    animate={isProductsInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                >
                    <Typography
                        align="center"
                        sx={{
                            fontWeight: 900,
                            mb: 2,
                            fontSize: { xs: "2.6rem", md: "4rem" },
                            letterSpacing: "-0.03em",
                            background:
                                "linear-gradient(135deg, #ff4d4d, #ff9999, #ffffff)",
                            WebkitBackgroundClip: "text",
                            color: "transparent",
                        }}
                    >
                        Our Tools Ecosystem
                    </Typography>

                    <Typography
                        align="center"
                        sx={{
                            mb: 10,
                            color: "rgba(255,255,255,0.75)",
                            fontSize: { xs: "1.2rem", md: "1.5rem" },
                            maxWidth: 680,
                            mx: "auto",
                            lineHeight: 1.6,
                        }}
                    >
                        Intelligent platforms engineered for creators, builders,
                        decision-makers, and learners.
                    </Typography>
                </motion.div>

                {/* ===== Cards Grid ===== */}
                <motion.div
                    variants={staggerGridVariants}
                    initial="hidden"
                    animate={isProductsInView ? "visible" : "hidden"}
                >
                    <Grid container spacing={5}>
                        {[
                            {
                                title: "Developers",
                                description:
                                    "APIs, SDKs, and frameworks for scalable, secure application development.",
                                icon: <Code />,
                                accent: "#ff4d4d",
                            },
                            {
                                title: "Designers",
                                description:
                                    "Creative systems, UI kits, and visual tools for modern experiences.",
                                icon: <Palette />,
                                accent: "#ff8080",
                            },
                            {
                                title: "Business Owners",
                                description:
                                    "Automation, analytics, and management tools to drive growth.",
                                icon: <BusinessCenterIcon />,
                                accent: "#ff6666",
                            },
                            {
                                title: "Students",
                                description:
                                    "Learning platforms and resources to accelerate skill mastery.",
                                icon: <School />,
                                accent: "#ff9999",
                            },
                        ].map((item, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    variants={gridItemVariants}
                                    whileHover={{ y: -14 }}
                                    transition={{ type: "spring", stiffness: 140 }}
                                    style={{ height: "100%" }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            p: 4,
                                            borderRadius: 4,
                                            position: "relative",
                                            overflow: "hidden",
                                            background:
                                                "linear-gradient(160deg, rgba(20,25,60,0.85), rgba(5,8,22,0.95))",
                                            backdropFilter: "blur(14px)",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                                            transition: "all 0.35s ease",

                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                inset: 0,
                                                background: `linear-gradient(120deg, transparent 30%, ${item.accent}22, transparent 70%)`,
                                                opacity: 0,
                                                transition: "opacity 0.35s ease",
                                            },

                                            "&:hover::before": {
                                                opacity: 1,
                                            },

                                            "&:hover": {
                                                border: `1px solid ${item.accent}`,
                                                boxShadow: `0 30px 80px ${item.accent}33`,
                                            },
                                        }}
                                    >
                                        {/* Icon */}
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                mb: 3,
                                                background: `${item.accent}22`,
                                                color: item.accent,
                                                boxShadow: `0 0 24px ${item.accent}66`,
                                                fontSize: 34,
                                            }}
                                        >
                                            {item.icon}
                                        </Box>

                                        {/* Title */}
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "1.35rem",
                                                mb: 1.5,
                                                color: "#fff",
                                            }}
                                        >
                                            {item.title}
                                        </Typography>

                                        {/* Description */}
                                        <Typography
                                            sx={{
                                                color: "rgba(255,255,255,0.75)",
                                                lineHeight: 1.65,
                                                fontSize: "1rem",
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                {/* ===== CTA ===== */}
                <Box sx={{ textAlign: "center", mt: 12 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isProductsInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.96 }}
                    >
                        <Button
                            component={Link}
                            to="/products-tools"
                            endIcon={<RocketLaunchIcon />}
                            sx={{
                                px: 6,
                                py: 1.6,
                                fontSize: "1.1rem",
                                fontWeight: 700,
                                borderRadius: 4,
                                textTransform: "none",
                                color: "#fff",
                                background:
                                    "linear-gradient(135deg, #ff4d4d, #ff8080)",
                                boxShadow: "0 18px 60px rgba(255,77,77,0.55)",
                                "&:hover": {
                                    background:
                                        "linear-gradient(135deg, #ff6666, #ff9999)",
                                    boxShadow: "0 24px 80px rgba(255,77,77,0.75)",
                                },
                            }}
                        >
                            Explore All Products
                        </Button>
                    </motion.div>
                </Box>
            </Container>



            {/* Thirukkural – Business Ethics */}
            <Container maxWidth="xl" sx={{ py: { xs: 6, md: 9 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                >
                    {/* Header */}
                    <Box textAlign="center" mb={6}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 900,
                                color: '#ff4d4d',
                                mb: 1,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Thirukkural on Business Ethics
                        </Typography>

                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.75)',
                                maxWidth: 820,
                                mx: 'auto',
                                lineHeight: 1.6,
                                fontSize: '1rem'
                            }}
                        >
                            Timeless Tamil wisdom shaping leadership, integrity, and sustainable business practices
                        </Typography>
                    </Box>

                    {/* Cards */}
                    <Grid container spacing={4}>
                        {[
                            {
                                tamil:
                                    'அறத்தினூஉங்கு ஆக்கம் இல்லை;\nஅதனை மறத்தலின் ஊக்கம் இல்லை',
                                english:
                                    'There is no greater gain than ethical conduct; abandoning it brings no lasting success.',
                                insight:
                                    'Ethics is the foundation of long-term business growth.'
                            },
                            {
                                tamil:
                                    'தொழில்நுட்பம் இல்லாதார் வாணிகம்\nநிலையாது வீழ்தல் உறும்',
                                english:
                                    'A business without wisdom and capability cannot sustain itself.',
                                insight:
                                    'Skill and knowledge are essential for business continuity.'
                            },
                            {
                                tamil:
                                    'நாணுடைமை யுடையவன் வாணிகம்\nபேணுதல் நலம் தரும்',
                                english:
                                    'Business led with integrity and responsibility yields lasting prosperity.',
                                insight:
                                    'Integrity-driven leadership creates enduring value.'
                            }
                        ].map((kural, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Card
                                        sx={{
                                            height: '100%',
                                            px: 4,
                                            py: 4.5,
                                            borderRadius: 3,
                                            background:
                                                'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                                            border: '1px solid rgba(255,255,255,0.18)',
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: '0 18px 36px rgba(139,0,0,0.28)',
                                                borderColor: '#ff4d4d'
                                            }
                                        }}
                                    >
                                        {/* Tamil */}
                                        <Typography
                                            sx={{
                                                color: 'white',
                                                fontWeight: 700,
                                                lineHeight: 1.75,
                                                fontSize: '1.1rem',
                                                whiteSpace: 'pre-line'
                                            }}
                                        >
                                            {kural.tamil}
                                        </Typography>

                                        {/* Divider */}
                                        <Box
                                            sx={{
                                                width: 46,
                                                height: 2,
                                                background: '#ff4d4d',
                                                mx: 'auto',
                                                my: 2
                                            }}
                                        />

                                        {/* English */}
                                        <Typography
                                            sx={{
                                                color: 'rgba(255,255,255,0.82)',
                                                lineHeight: 1.6,
                                                fontStyle: 'italic',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            {kural.english}
                                        </Typography>

                                        {/* Insight */}
                                        <Typography
                                            sx={{
                                                mt: 3,
                                                fontSize: '0.85rem',
                                                color: '#ff4d4d',
                                                fontWeight: 600,
                                                letterSpacing: '0.04em'
                                            }}
                                        >
                                            {kural.insight}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>


            {/* ================= LEADERSHIP & MANAGEMENT ================= */}
            <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    viewport={{ once: true }}
                >
                    {/* ===== SECTION HEADER ===== */}
                    <Box textAlign="center" mb={10}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 900,
                                color: "#ff4d4d",
                                letterSpacing: "-0.02em",
                                mb: 1,
                            }}
                        >
                            Leadership & Management
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 820,
                                mx: "auto",
                                color: "rgba(255,255,255,0.75)",
                                fontSize: "1.05rem",
                                lineHeight: 1.7,
                            }}
                        >
                            A collaborative leadership council combining vision, strategy,
                            academic excellence, security governance, and operational execution—
                            working together toward sustainable institutional growth.
                        </Typography>
                    </Box>

                    {/* ================= FOUNDER ================= */}
                    <Box mb={12}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <Card
                                sx={{
                                    maxWidth: 900,
                                    mx: "auto",
                                    p: { xs: 4, md: 5 },
                                    borderRadius: 4,
                                    textAlign: "center",
                                    background:
                                        "linear-gradient(135deg, rgba(139,0,0,0.25), rgba(0,0,0,0.6))",
                                    border: "2px solid rgba(139,0,0,0.55)",
                                    boxShadow: "0 26px 55px rgba(139,0,0,0.4)",
                                }}
                            >
                                <Typography variant="h4" sx={{ fontWeight: 900, color: "#ff4d4d" }}>
                                    Dinesh M
                                </Typography>

                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        color: "white",
                                        letterSpacing: "0.12em",
                                        mt: 0.5,
                                        mb: 2,
                                    }}
                                >
                                    Founder · Chief Visionary · Technologist
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.9)",
                                        lineHeight: 1.8,
                                        fontSize: "1.05rem",
                                        maxWidth: 760,
                                        mx: "auto",
                                    }}
                                >
                                    Provides long-term vision, digital architecture leadership, and
                                    technology direction—enabling scalable, future-ready platforms
                                    with institutional integrity.
                                </Typography>
                            </Card>
                        </motion.div>
                    </Box>

                    {/* ================= CORE TEAM ================= */}
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: "white", mb: 4 }}
                    >
                        Leadership Council & Core Team
                    </Typography>

                    <Grid container spacing={3} alignItems="stretch">
                        {[{
                            name: "Govarthan S",
                            role: "Technical Consultant",
                            domain: "Technology & Development",
                            desc:
                                "Builds scalable application systems with strong focus on performance, reliability, and clean architecture.",
                        },
                        {
                            name: "Dhanalakshmi M",
                            role: "Technical Consultant",
                            domain: "Technology & Development",
                            desc:
                                "Supports platform development and feature execution across modern software systems.",
                        },
                        {
                            name: "Dr. K. Balaji",
                            role: "Strategic & Academic Operations Lead",
                            domain: "Academic Governance",
                            desc:
                                "Leads academic strategy and operational alignment, ensuring institutional learning frameworks remain industry-relevant and scalable.",
                        },
                        {
                            name: "Dr. Githendiran K",
                            role: "Strategy & Operations Advisor",
                            domain: "Institutional Governance",
                            desc:
                                "Advises on long-term strategy, compliance, and ethical leadership for sustainable organizational growth.",
                        },
                        {
                            name: "Premkumar S",
                            role: "Security Consultant",
                            domain: "Technology & Risk",
                            desc:
                                "Oversees security architecture, compliance frameworks, and enterprise-level risk mitigation strategies.",
                        },
                        {
                            name: "Vishweshwaran P",
                            role: "Operations & Team Manager",
                            domain: "Execution & Delivery",
                            desc:
                                "Ensures structured execution, cross-team coordination, and consistent operational delivery.",
                        },

                        ].map((member, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <motion.div
                                    initial={{ opacity: 0, y: 25 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.45, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    style={{ height: "100%" }}
                                >
                                    <Card
                                        sx={{
                                            height: "100%",
                                            p: 3,
                                            borderRadius: 3,
                                            display: "flex",
                                            flexDirection: "column",
                                            background: "rgba(255,255,255,0.04)",
                                            border: "1px solid rgba(255,255,255,0.15)",
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-6px)",
                                                borderColor: "#ff4d4d",
                                                boxShadow: "0 16px 36px rgba(139,0,0,0.28)",
                                            },
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 800, color: "white", mb: 0.4 }}>
                                            {member.name}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#ff4d4d",
                                                fontWeight: 700,
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            {member.role}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "0.78rem",
                                                color: "rgba(255,255,255,0.65)",
                                                letterSpacing: "0.06em",
                                                textTransform: "uppercase",
                                                mt: 0.4,
                                                mb: 1.4,
                                            }}
                                        >
                                            {member.domain}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "0.9rem",
                                                color: "rgba(255,255,255,0.85)",
                                                lineHeight: 1.65,
                                                flexGrow: 1,
                                            }}
                                        >
                                            {member.desc}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>




            <EntrepreneurMotivation />


            {/* FAQ Accordion Section */}
            <Container maxWidth="lg" sx={{ py: 15, position: 'relative' }}>
                <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    viewport={{ once: true }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            fontWeight: 900,
                            mb: 6,
                            background:
                                'linear-gradient(135deg, #8b0000 0%, #ff4d4d 50%, #ff9999 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            letterSpacing: '-0.02em',
                        }}
                    >
                        FAQ
                    </Typography>

                    <Typography
                        variant="h6"
                        align="center"
                        sx={{
                            mb: 8,
                            color: 'rgba(255,255,255,0.85)',
                            fontWeight: 300,
                            fontSize: { xs: '1.2rem', md: '1.5rem' },
                            maxWidth: '700px',
                            mx: 'auto',
                        }}
                    >
                        Everything you need to know about our services and partnerships.
                    </Typography>

                    {/* FAQ Accordions */}
                    <Box
                        sx={{
                            background:
                                'linear-gradient(135deg, rgba(26, 31, 61, 0.9), rgba(10, 17, 40, 0.95))',
                            borderRadius: 4,
                            border: '1px solid rgba(255,255,255,0.1)',
                            p: 3,
                            boxShadow: '0 10px 30px rgba(139, 0, 0, 0.3)',
                        }}
                    >
                        {[
                            {
                                q: 'What comprehensive services does Zolang Nexora offer?',
                                a: 'We provide end-to-end digital transformation services including enterprise software development, cloud-native applications, AI/ML integration, cybersecurity, and strategic IT consulting.',
                            },
                            {
                                q: 'How do we initiate collaboration with your team?',
                                a: 'Start with a complimentary discovery session where we assess your requirements, define scope, and plan project execution using an agile approach.',
                            },
                            {
                                q: 'What ongoing support do you provide?',
                                a: 'We offer managed services including 24/7 monitoring, proactive security updates, performance optimization, and continuous innovation.',
                            },
                            {
                                q: 'Which industries are your core expertise?',
                                a: 'Fintech, Healthcare, EdTech, eCommerce, IoT, and SaaS — we deliver tailored, regulation-compliant solutions for diverse sectors.',
                            },
                            {
                                q: 'How do you integrate with existing systems?',
                                a: 'We integrate seamlessly with in-house teams, providing flexible engagement models from dedicated teams to project-based delivery.',
                            },
                        ].map((item, index) => (
                            <Accordion
                                key={index}
                                sx={{
                                    background: 'transparent',
                                    color: 'white',
                                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ExpandMoreIcon
                                            sx={{ color: '#ff4d4d', fontSize: '2rem' }}
                                        />
                                    }
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            background: 'linear-gradient(135deg, #ff4d4d, #ff9999)',
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            color: 'transparent',
                                            fontSize: '1.3rem',
                                        }}
                                    >
                                        {item.q}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'rgba(255,255,255,0.85)',
                                            lineHeight: 1.7,
                                            fontSize: '1.05rem',
                                        }}
                                    >
                                        {item.a}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </motion.div>
            </Container>

        </Box>
    );
};

export default Home;